/**
 * Past trips, and the one number the whole system is judged on: realised against predicted.
 *
 * ALGORITHM.md §16 calls that gap the only metric that matters, which makes the realised-discount
 * field the most important input on this screen rather than an afterthought — so it sits on the row
 * itself, prefilled with nothing, ready for the figure off the receipt.
 */
import { useEffect, useState } from "react";
import { api, errorMessage } from "../api.ts";
import { euros, maskedCard, parseAmountCents, signedEuros, stamp } from "../format.ts";
import { useApi } from "../hooks.ts";
import { useModel } from "../model.tsx";
import { Icon } from "../components/icons.tsx";
import { Badge, Empty, Note, Spinner, Stat } from "../components/ui.tsx";
import type { ReceiptSyncResult, Trip } from "../../../server/types.ts";

const PAGE = 25;

/**
 * Six counters, one line of prose. A sweep that found nothing has to say so as plainly as one that
 * found something, or the button leaves the user guessing whether it worked.
 */
function describeSync(result: ReceiptSyncResult): string {
	const parts: string[] = [];
	if (result.receiptsNew > 0) {
		parts.push(`${result.receiptsNew} new ${result.receiptsNew === 1 ? "receipt" : "receipts"}`);
		parts.push(`${result.tripsMatched} matched to a trip`);
	}
	if (result.pushed > 0) parts.push(`${result.pushed} handed to the receipt splitter`);
	if (result.pushFailures > 0) parts.push(`${result.pushFailures} the splitter would not take`);
	if (parts.length === 0) {
		return `Nothing new on ${result.accountsScanned} ${result.accountsScanned === 1 ? "card" : "cards"}.`;
	}
	return `${parts.join(", ")}.`;
}

export function History() {
	const model = useModel();
	const [offset, setOffset] = useState(0);
	const [trips, setTrips] = useState<Trip[]>([]);
	const [end, setEnd] = useState(false);
	const [drafts, setDrafts] = useState<Map<number, string>>(new Map());
	const [open, setOpen] = useState<number | null>(null);
	const [busy, setBusy] = useState<number | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [syncing, setSyncing] = useState(false);
	const [sync, setSync] = useState<ReceiptSyncResult | null>(null);
	const page = useApi((signal) => api.trips(PAGE, offset, { signal }), [offset]);

	useEffect(() => {
		const batch = page.data;
		if (batch === null) return;
		if (batch.length < PAGE) setEnd(true);
		setTrips((current) => {
			const seen = new Set(current.map((trip) => trip.id));
			return [...current, ...batch.filter((trip) => !seen.has(trip.id))];
		});
	}, [page.data]);

	const accounts = model.boot?.accounts ?? [];

	const save = async (trip: Trip, text: string): Promise<void> => {
		const cents = parseAmountCents(text);
		if (cents === null) {
			setError(`"${text}" is not an amount. Use euros, like 2,15.`);
			return;
		}
		setBusy(trip.id);
		setError(null);
		try {
			const updated = await api.setRealised(trip.id, cents);
			setTrips((current) => current.map((row) => (row.id === updated.id ? updated : row)));
			setDrafts((current) => {
				const next = new Map(current);
				next.delete(trip.id);
				return next;
			});
		} catch (err) {
			setError(errorMessage(err));
		} finally {
			setBusy(null);
		}
	};

	/**
	 * The nightly sweep does this unattended; the button exists because the moment that matters is
	 * minutes after the shop, when the till receipt is the only proof of what the card really saved.
	 */
	const fetchReceipts = async (): Promise<void> => {
		setSyncing(true);
		setSync(null);
		setError(null);
		try {
			const result = await api.syncReceipts();
			setSync(result);
			// Realised figures may have just been filled in from the receipts, so re-read page one.
			setTrips([]);
			setEnd(false);
			if (offset === 0) page.reload();
			else setOffset(0);
		} catch (err) {
			setError(errorMessage(err));
		} finally {
			setSyncing(false);
		}
	};

	const scored = trips.filter((trip) => trip.realisedDiscountCents !== null);
	const predictedSum = scored.reduce((sum, trip) => sum + trip.predictedDiscountCents, 0);
	const realisedSum = scored.reduce((sum, trip) => sum + (trip.realisedDiscountCents ?? 0), 0);

	return (
		<main className="screen stack">
			<section className="panel">
				<h1 className="panel-title">Predicted against realised</h1>
				{scored.length === 0 ? (
					<p className="panel-lede">
						No trip has a receipt figure yet. Fetch receipts reads them off the cards themselves, or type one
						in by hand — either way this becomes the only honest read on whether the valuation model is right.
					</p>
				) : (
					<div className="stats">
						<Stat label="Trips scored" value={`${scored.length} of ${trips.length}`} />
						<Stat label="Predicted" value={euros(predictedSum)} />
						<Stat label="Realised" value={euros(realisedSum)} />
						<Stat
							label="Gap"
							value={signedEuros(realisedSum - predictedSum)}
							hint={predictedSum === 0 ? undefined : `${Math.round((realisedSum / predictedSum - 1) * 100)}%`}
						/>
					</div>
				)}

				<button type="button" className="btn btn-quiet" disabled={syncing} onClick={() => void fetchReceipts()}>
					<Icon name="refresh" /> {syncing ? "Reading receipts…" : "Fetch receipts"}
				</button>
				{sync !== null && (
					<Note tone={sync.pushFailures > 0 ? "warn" : "neutral"}>{describeSync(sync)}</Note>
				)}
			</section>

			{error !== null && <Note tone="danger">{error}</Note>}
			{page.error !== null && <Note tone="danger">{page.error}</Note>}

			{trips.length === 0 && page.loading && (
				<div className="loading">
					<Spinner label="Reading trip history" />
				</div>
			)}
			{trips.length === 0 && !page.loading && page.error === null && (
				<Empty title="No trips yet">
					Every committed basket lands here with its lines and its predicted discount. That record is what teaches
					category inference, similarity and the slot cost, so nothing here is bookkeeping.
				</Empty>
			)}

			<ul className="trips">
				{trips.map((trip) => {
					const account = accounts.find((row) => row.id === trip.accountId) ?? null;
					const category = model.categories.find((row) => row.id === trip.categoryId) ?? null;
					const realised = trip.realisedDiscountCents;
					const draft = drafts.get(trip.id) ?? "";
					const expanded = open === trip.id;
					return (
						<li key={trip.id} className="trip">
							<button
								type="button"
								className="trip-head"
								aria-expanded={expanded}
								onClick={() => setOpen(expanded ? null : trip.id)}
							>
								<span className="trip-when">{stamp(trip.ts)}</span>
								<span className="trip-total">{euros(trip.totalCents)}</span>
								<span className="trip-badges">
									<Badge>{category?.name ?? "no category"}</Badge>
									<Badge tone={trip.regime === "HARVEST" ? "money" : "accent"}>{trip.regime.toLowerCase()}</Badge>
									<Badge>tier {trip.tier}</Badge>
								</span>
								<span className="trip-card">{account === null ? `account ${trip.accountId}` : maskedCard(account.bonusCard)}</span>
								<span className="trip-figures">
									<span className="trip-pred">
										<small>predicted</small>
										{euros(trip.predictedDiscountCents)}
									</span>
									<span className={realised === null ? "trip-real trip-real-missing" : "trip-real"}>
										<small>realised</small>
										{realised === null ? "\u2014" : euros(realised)}
									</span>
									{realised !== null && (
										<Badge tone={realised >= trip.predictedDiscountCents ? "money" : "warn"}>
											{signedEuros(realised - trip.predictedDiscountCents)}
										</Badge>
									)}
								</span>
								<Icon name="chevron" />
							</button>

							<form
								className="trip-entry"
								onSubmit={(event) => {
									event.preventDefault();
									void save(trip, draft);
								}}
							>
								<label htmlFor={`real-${trip.id}`}>
									{realised === null ? "Discount off the receipt" : "Correct the receipt figure"}
								</label>
								<input
									id={`real-${trip.id}`}
									value={draft}
									inputMode="decimal"
									autoComplete="off"
									placeholder={realised === null ? "0,00" : euros(realised)}
									onChange={(event) =>
										setDrafts((current) => new Map(current).set(trip.id, event.target.value))
									}
								/>
								<button type="submit" className="btn btn-quiet" disabled={busy === trip.id || draft.trim() === ""}>
									{busy === trip.id ? <Spinner label="Saving" /> : "Save"}
								</button>
							</form>

							{expanded && (
								<ul className="trip-lines">
									{trip.lines.map((line, index) => (
										<li key={`${line.barcode}-${index}`}>
											<span className="tl-title">{line.title}</span>
											<span className="tl-qty">{line.qty}&times;</span>
											<span className="tl-price">{euros(line.unitPriceCents)}</span>
											<span className={line.discountCents > 0 ? "tl-disc tl-disc-on" : "tl-disc"}>
												{line.discountCents > 0 ? `\u2212${euros(line.discountCents)}` : ""}
											</span>
											{line.offerHqId !== null && <code className="tl-offer">{line.offerHqId}</code>}
											{line.productId === null && <Badge tone="warn">unknown</Badge>}
										</li>
									))}
								</ul>
							)}
						</li>
					);
				})}
			</ul>

			{!end && trips.length > 0 && (
				<button
					type="button"
					className="btn btn-ghost"
					disabled={page.loading}
					onClick={() => setOffset(trips.length)}
				>
					{page.loading ? <Spinner label="Loading" /> : `Load ${PAGE} more`}
				</button>
			)}
		</main>
	);
}
